import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useApi } from '../../hooks/useApi';
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import FilterDropdown from '../common/FilterDropdown';

const SchoolFinancialLedger = () => {
    const { get } = useApi();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // 'success', 'pending', 'failed'
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const fetchTransactions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await get('/api/finance/mpesa-transactions/');
            setTransactions(response || []);
        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const sortedAndFilteredTransactions = useMemo(() => {
        let result = transactions.filter(item => {
            const matchesSearch =
                item.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.admission_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.phone_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = !statusFilter || item.status === statusFilter;

            return matchesSearch && matchesStatus;
        });

        if (sortConfig.key) {
            result.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // handle null receipt numbers
                if (!aVal) aVal = '';
                if (!bVal) bVal = '';

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [transactions, searchTerm, statusFilter, sortConfig]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, sortConfig]);

    // Pagination Logic
    const totalPages = Math.ceil(sortedAndFilteredTransactions.length / itemsPerPage);
    const paginatedTransactions = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedAndFilteredTransactions.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedAndFilteredTransactions, currentPage]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        if (status === 'success') {
            return <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest rounded-full border border-emerald-100">Success</span>;
        }
        if (status === 'failed') {
            return <span className="px-3 py-1 bg-rose-50 text-rose-600 text-[10px] font-black uppercase tracking-widest rounded-full border border-rose-100">Failed</span>;
        }
        return <span className="px-3 py-1 bg-amber-50 text-amber-600 text-[10px] font-black uppercase tracking-widest rounded-full border border-amber-100">Pending</span>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18216D]"></div>
            </div>
        );
    }

    const exportToExcel = () => {
        const data = sortedAndFilteredTransactions.map(t => ({
            'Student Name': t.student_name || '',
            'Admission Number': t.admission_number || '',
            'Phone Number': t.phone_number || '',
            'Amount': t.amount || 0,
            'Status': t.status || '',
            'Date': formatDate(t.created_at),
            'Receipt Number': t.receipt_number || '',
            'Transaction Reference': t.transaction_reference || ''
        }));
        
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
        
        // Adjust column widths
        const wscols = [
            { wch: 25 }, // Student Name
            { wch: 20 }, // Admission Number
            { wch: 15 }, // Phone Number
            { wch: 10 }, // Amount
            { wch: 10 }, // Status
            { wch: 20 }, // Date
            { wch: 15 }, // Receipt Number
            { wch: 25 }  // Transaction Reference
        ];
        worksheet['!cols'] = wscols;

        XLSX.writeFile(workbook, "student_accounts_export.xlsx");
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                    <div>
                        <h2 className="text-xl font-black text-[#18216D] tracking-tight italic">Student Accounts</h2>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">M-Pesa STK Push Transactions</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={exportToExcel}
                        className="px-6 py-3 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 border border-emerald-200 transition-all font-black text-xs uppercase tracking-widest flex items-center space-x-2"
                    >
                        <i className="fas fa-file-excel"></i>
                        <span>Export</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative group md:col-span-3">
                    <i className="fas fa-search absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"></i>
                    <input
                        type="text"
                        placeholder="Search student, admission no, phone, or receipt..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-12 pr-6 py-4 bg-white border-2 border-slate-200 rounded-2xl focus:border-[#FFC425] transition-all text-sm font-medium text-[#18216D] placeholder-slate-300 outline-none"
                    />
                </div>
                <FilterDropdown
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                        { name: 'All Statuses', value: '' },
                        { name: 'Success', value: 'success' },
                        { name: 'Pending', value: 'pending' },
                        { name: 'Failed', value: 'failed' }
                    ]}
                />
            </div>

            {/* Table */}
            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-indigo-900/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50/50 border-b border-slate-100">
                            <tr>
                                <th onClick={() => requestSort('student_name')} className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:text-[#18216D]">
                                    <div className="flex items-center gap-2">Scholar Identity <ArrowsUpDownIcon className="w-3 h-3" /></div>
                                </th>
                                <th onClick={() => requestSort('phone_number')} className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:text-[#18216D]">
                                    <div className="flex items-center gap-2">Payer Phone <ArrowsUpDownIcon className="w-3 h-3" /></div>
                                </th>
                                <th onClick={() => requestSort('created_at')} className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:text-[#18216D]">
                                    <div className="flex items-center gap-2">Payment Date <ArrowsUpDownIcon className="w-3 h-3" /></div>
                                </th>
                                <th onClick={() => requestSort('amount')} className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right cursor-pointer hover:text-[#18216D]">
                                    <div className="flex items-center justify-end gap-2">Amount <ArrowsUpDownIcon className="w-3 h-3" /></div>
                                </th>
                                <th onClick={() => requestSort('receipt_number')} className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center cursor-pointer hover:text-[#18216D]">
                                    <div className="flex items-center justify-center gap-2">Transaction Code <ArrowsUpDownIcon className="w-3 h-3" /></div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {paginatedTransactions.map((item) => (
                                <tr key={item.id} className="hover:bg-indigo-50/30 transition-all group">
                                    <td className="px-8 py-5">
                                        <div>
                                            <p className="font-black text-[#18216D] text-sm italic">{item.student_name || 'Unknown Student'}</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{item.admission_number || 'N/A'}</p>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className="text-sm font-bold text-slate-600">{item.phone_number}</span>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className="text-xs font-bold text-slate-500">{formatDate(item.created_at)}</span>
                                    </td>
                                    <td className="px-8 py-5 text-right font-bold text-sm text-emerald-600">
                                        {formatCurrency(item.amount)}
                                    </td>
                                    <td className="px-8 py-5 text-center">
                                        {item.status === 'success' ? (
                                            <span className="text-[10px] font-black text-[#18216D] uppercase bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">{item.receipt_number || 'N/A'}</span>
                                        ) : (
                                            getStatusBadge(item.status)
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {paginatedTransactions.length > 0 && (
                    <div className="flex items-center justify-between border-t border-slate-100 pt-6 px-6 pb-6 bg-slate-50/50">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedAndFilteredTransactions.length)} of {sortedAndFilteredTransactions.length} records
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-black uppercase tracking-widest text-[#18216D] disabled:opacity-50 hover:bg-slate-50 transition-colors"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-black uppercase tracking-widest text-[#18216D] disabled:opacity-50 hover:bg-slate-50 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}

                {sortedAndFilteredTransactions.length === 0 && (
                    <div className="py-20 text-center">
                        <FunnelIcon className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">No transactions match your filters</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SchoolFinancialLedger;
